import React from 'react';
import { ScatterPlot, DestinationScatterPlot, TrajectoryPlot, HeatmapPlot, GridPlot } from '../visualizations';
import { VisualizationEvent } from '../types';
import { UseMapVisualizationReturn } from '../../../hooks/useMapVisualization';

interface VisualizationOverlaysProps {
  events: VisualizationEvent[];
  mapVisualization: UseMapVisualizationReturn;
  teamId?: string; // Optional: filter events by team
  prefix?: string; // Optional: prefix for keys (e.g., 'home', 'away', 'global')
}

/**
 * VisualizationOverlays - Renders all visualization overlays on a pitch
 * Handles both per-stat-type mapping and global visualization type settings
 */
const VisualizationOverlays: React.FC<VisualizationOverlaysProps> = ({
  events,
  mapVisualization,
  teamId,
  prefix = '',
}) => {
  const {
    visualizationType,
    filters,
    statTypeVisualizationMap,
    pointSelection,
    trajectoryDensity,
    heatmapRadius,
    heatmapSmoothness,
    heatmapOpacity,
    gridSize,
    gridShowLabels,
    gridOpacity,
    whiteBackground,
    colorBy,
    teamColors,
    statTypeColors,
    scatterShapes,
    trajectoryStyles,
    shapesKey,
    stylesKey,
    normalizeToFirstHalf,
  } = mapVisualization;

  // Filter events by team if teamId is provided
  const teamEvents = teamId ? events.filter(e => e.team_id === teamId) : events;

  // If per-stat-type visualization mapping is active, use it
  if (filters.stat_type_id && filters.stat_type_id.length > 0 && Object.keys(statTypeVisualizationMap).length > 0) {
    const scatterEvents = teamEvents.filter((event) => {
      const vizTypes = statTypeVisualizationMap[event.stat_type_id] || [];
      return vizTypes.includes('scatter') || vizTypes.length === 0; // Default to scatter if not mapped
    });
    const trajectoryEvents = teamEvents.filter((event) => {
      const vizTypes = statTypeVisualizationMap[event.stat_type_id] || [];
      return vizTypes.includes('trajectory');
    });
    const heatmapEvents = teamEvents.filter((event) => {
      const vizTypes = statTypeVisualizationMap[event.stat_type_id] || [];
      return vizTypes.includes('heatmap');
    });
    const gridEvents = teamEvents.filter((event) => {
      const vizTypes = statTypeVisualizationMap[event.stat_type_id] || [];
      return vizTypes.includes('grid');
    });

    return (
      <>
        {/* Heatmap - Bottom layer */}
        {heatmapEvents.length > 0 && (
          <HeatmapPlot
            key={`heatmap-${prefix}-${heatmapRadius}-${heatmapSmoothness}-norm-${normalizeToFirstHalf}`}
            events={heatmapEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            radius={heatmapRadius}
            smoothness={heatmapSmoothness}
            opacity={heatmapOpacity}
            pointSelection={pointSelection}
          />
        )}
        {/* Grid - Above heatmap */}
        {gridEvents.length > 0 && (
          <GridPlot
            key={`grid-${prefix}-${gridSize}-norm-${normalizeToFirstHalf}-${pointSelection.join(',')}`}
            events={gridEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            gridSize={gridSize}
            showLabels={gridShowLabels}
            opacity={gridOpacity}
            whiteBackground={whiteBackground}
            pointSelection={pointSelection}
          />
        )}
        {/* Trajectory plots - Middle layer */}
        {trajectoryEvents.length > 0 && (
          <TrajectoryPlot
            key={`trajectory-${prefix}-${stylesKey}`}
            events={trajectoryEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            trajectoryStyles={trajectoryStyles}
            strokeWidth={3}
            density={trajectoryDensity}
          />
        )}
        {/* Scatter plots - Top layer */}
        {scatterEvents.length > 0 && (
          <>
            {pointSelection.includes('source') && (
              <ScatterPlot
                key={`scatter-source-${prefix}-${shapesKey}`}
                events={scatterEvents}
                colorBy={colorBy}
                teamColors={teamColors}
                statTypeColors={statTypeColors}
                scatterShapes={scatterShapes}
                pointSize={6}
              />
            )}
            {pointSelection.includes('destination') && (
              <DestinationScatterPlot
                key={`scatter-dest-${prefix}-${shapesKey}`}
                events={scatterEvents}
                colorBy={colorBy}
                teamColors={teamColors}
                statTypeColors={statTypeColors}
                scatterShapes={scatterShapes}
                pointSize={6}
              />
            )}
          </>
        )}
      </>
    );
  } else {
    // Use the global visualization type setting
    return (
      <>
        {/* Heatmap - Bottom layer */}
        {visualizationType.includes('heatmap') && (
          <HeatmapPlot
            key={`heatmap-global-${prefix}-${heatmapRadius}-${heatmapSmoothness}-norm-${normalizeToFirstHalf}`}
            events={teamEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            radius={heatmapRadius}
            smoothness={heatmapSmoothness}
            opacity={heatmapOpacity}
            pointSelection={pointSelection}
          />
        )}
        {/* Grid - Above heatmap */}
        {visualizationType.includes('grid') && (
          <GridPlot
            key={`grid-global-${prefix}-${gridSize}-norm-${normalizeToFirstHalf}-${pointSelection.join(',')}`}
            events={teamEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            gridSize={gridSize}
            showLabels={gridShowLabels}
            opacity={gridOpacity}
            whiteBackground={whiteBackground}
            pointSelection={pointSelection}
          />
        )}
        {/* Trajectory plots - Middle layer */}
        {visualizationType.includes('trajectory') && (
          <TrajectoryPlot
            key={`trajectory-global-${prefix}-${stylesKey}`}
            events={teamEvents}
            colorBy={colorBy}
            teamColors={teamColors}
            statTypeColors={statTypeColors}
            trajectoryStyles={trajectoryStyles}
            strokeWidth={3}
            density={trajectoryDensity}
          />
        )}
        {/* Scatter plots - Top layer */}
        {visualizationType.includes('scatter') && (
          <>
            {pointSelection.includes('source') && (
              <ScatterPlot
                key={`scatter-source-global-${prefix}-${shapesKey}`}
                events={teamEvents}
                colorBy={colorBy}
                teamColors={teamColors}
                statTypeColors={statTypeColors}
                scatterShapes={scatterShapes}
                pointSize={6}
              />
            )}
            {pointSelection.includes('destination') && (
              <DestinationScatterPlot
                key={`scatter-dest-global-${prefix}-${shapesKey}`}
                events={teamEvents}
                colorBy={colorBy}
                teamColors={teamColors}
                statTypeColors={statTypeColors}
                scatterShapes={scatterShapes}
                pointSize={6}
              />
            )}
          </>
        )}
      </>
    );
  }
};

export default VisualizationOverlays;

